import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { MapPin, Briefcase, Clock, ArrowRight, Search, Filter } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import axios from 'axios';
import SEO from '@/components/SEO';

const API = '/api';

const sectorFilters = ['All', 'Construction', 'Engineering', 'Trades', 'Industrial', 'Infrastructure'];

export default function Jobs() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSector, setSelectedSector] = useState('All');

  useEffect(() => {
    const fetchJobs = async () => {
      try {
        const response = await axios.get(`${API}/jobs`);
        setJobs(response.data);
      } catch (error) {
        console.error('Error fetching jobs:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchJobs();
  }, []);

  const filteredJobs = jobs.filter(job => {
    const matchesSearch = job.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          job.location.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesSector = selectedSector === 'All' || job.sector === selectedSector;
    return matchesSearch && matchesSector;
  });

  return (
    <div data-testid="jobs-page">
      <SEO page="jobs" />
      {/* Hero */}
      <section className="pt-32 pb-20 bg-slate-900">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <p className="text-[#F5A623] font-semibold uppercase tracking-wider mb-4">Current Opportunities</p>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-white uppercase mb-6">
              Find Your Next Role
            </h1>
            <p className="text-slate-300 text-lg leading-relaxed">
              Browse our current job opportunities across construction, engineering, trades and industrial sectors. Don't see the right role? Register your interest and we'll be in touch when something suitable comes up.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Search & Filter */}
      <section className="py-8 bg-white border-b border-slate-200 sticky top-20 z-40">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row gap-4 items-center">
            <div className="relative flex-1 w-full">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                type="text"
                placeholder="Search by job title or location..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-12 h-12 border-slate-300 w-full"
                data-testid="job-search-input"
              />
            </div>
            <div className="flex items-center gap-2 overflow-x-auto w-full md:w-auto">
              <Filter className="w-5 h-5 text-slate-500 flex-shrink-0" />
              {sectorFilters.map((sector) => (
                <button
                  key={sector}
                  onClick={() => setSelectedSector(sector)}
                  className={`px-4 py-2 text-sm font-semibold whitespace-nowrap transition-colors ${
                    selectedSector === sector
                      ? 'bg-[#1A8B9D] text-white'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                  data-testid={`filter-${sector.toLowerCase()}`}
                >
                  {sector}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Jobs List */}
      <section className="py-12 md:py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin w-8 h-8 border-2 border-[#1A8B9D] border-t-transparent rounded-full mx-auto mb-4"></div>
              <p className="text-slate-600">Loading jobs...</p>
            </div>
          ) : filteredJobs.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-slate-600 mb-4">No jobs found matching your criteria.</p>
              <Link to="/candidates">
                <Button className="bg-[#1A8B9D] hover:bg-[#115E6B] text-white font-bold uppercase tracking-wide">
                  Register Your Interest
                </Button>
              </Link>
            </div>
          ) : (
            <>
              <p className="text-slate-600 mb-8">{filteredJobs.length} job{filteredJobs.length !== 1 ? 's' : ''} found</p>
              <div className="space-y-4">
                {filteredJobs.map((job, idx) => (
                  <motion.div
                    key={job.id}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: idx * 0.05 }}
                    className="bg-white p-6 md:p-8 border border-slate-200 hover:border-[#1A8B9D]/50 hover:shadow-lg transition-all"
                    data-testid={`job-card-${job.id}`}
                  >
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <span className="bg-teal-50 text-[#1A8B9D] text-xs font-bold px-3 py-1 uppercase tracking-wide">
                            {job.sector}
                          </span>
                          <span className="bg-slate-100 text-slate-600 text-xs font-bold px-3 py-1 uppercase tracking-wide">
                            {job.job_type}
                          </span>
                        </div>
                        <h3 className="font-heading text-xl md:text-2xl font-bold text-slate-900 uppercase mb-2">
                          {job.title}
                        </h3>
                        <p className="text-slate-600 mb-4">{job.description}</p>
                        <div className="flex flex-wrap items-center gap-4 text-sm text-slate-500">
                          <div className="flex items-center gap-1">
                            <MapPin className="w-4 h-4" />
                            {job.location}
                          </div>
                          <div className="flex items-center gap-1">
                            <Briefcase className="w-4 h-4" />
                            {job.job_type}
                          </div>
                          <div className="flex items-center gap-1">
                            <Clock className="w-4 h-4" />
                            Posted {job.posted_date}
                          </div>
                        </div>
                      </div>
                      <div className="flex-shrink-0">
                        <Link to="/candidates#register">
                          <Button className="bg-[#1A8B9D] hover:bg-[#115E6B] text-white font-bold uppercase tracking-wide">
                            Apply Now <ArrowRight className="ml-2 w-4 h-4" />
                          </Button>
                        </Link>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-[#0F172A]">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-white uppercase mb-4">
            Don't See The Right Role?
          </h2>
          <p className="text-slate-400 mb-8">
            Register your interest and we'll contact you when a suitable opportunity becomes available.
          </p>
          <Link to="/candidates#register">
            <Button className="bg-[#F5A623] hover:bg-[#D98E15] text-slate-900 font-bold py-4 px-8 uppercase tracking-wide">
              Register Your Interest
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
